// src/components/CacheBuster.js
import React, { useState, useEffect, useCallback } from 'react';

const CACHE_BUSTER_STORAGE_KEY = 'app_version_cache';
const RELOAD_COOLDOWN_KEY = 'app_reload_cooldown';

const CacheBuster = ({ children, currentAppVersion, loadingComponent = null }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [reloadFn, setReloadFn] = useState(null);

  // Очистка всех кешей
  const clearAllCaches = useCallback(async () => {
    console.log('[CacheBuster] Очистка кешей...');
    
    try {
      // 1. Очищаем localStorage
      localStorage.removeItem(CACHE_BUSTER_STORAGE_KEY);
      
      // 2. Очищаем sessionStorage
      sessionStorage.clear();
      
      // 3. Очищаем Service Worker кеши (если есть)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log('[CacheBuster] Удаляем кеш:', cacheName);
            return caches.delete(cacheName);
          })
        );
      }
      
      // 4. Очищаем кеш браузера через Cache API
      if ('caches' in window) {
        await caches.delete('v1');
      }
      
      console.log('[CacheBuster] Все кеши очищены');
      return true;
    } catch (error) {
      console.error('[CacheBuster] Ошибка очистки кешей:', error);
      return false;
    }
  }, []);

  // Принудительная перезагрузка страницы
  const forceReload = useCallback(() => {
    console.log('[CacheBuster] Принудительная перезагрузка страницы...');
    
    // Добавляем параметр времени, чтобы избежать кеширования
    const url = new URL(window.location.href);
    url.searchParams.set('_t', Date.now());
    
    // Сохраняем флаг, что перезагрузка была выполнена
    sessionStorage.setItem(RELOAD_COOLDOWN_KEY, Date.now().toString());
    
    window.location.href = url.toString();
  }, []);

  // Проверка версии
  const checkVersion = useCallback(async () => {
    setIsChecking(true);
    
    // Защита от бесконечной перезагрузки (cooldown 10 секунд)
    const lastReload = sessionStorage.getItem(RELOAD_COOLDOWN_KEY);
    if (lastReload && Date.now() - parseInt(lastReload) < 10000) {
      console.log('[CacheBuster] Недавно была перезагрузка, пропускаем проверку');
      setIsChecking(false);
      return;
    }
    
    try {
      // Загружаем meta.json с сервера (с параметром против кеширования)
      const response = await fetch(`/meta.json?_=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      const latestVersion = data.version;
      
      console.log(`[CacheBuster] Текущая версия: ${currentAppVersion}`);
      console.log(`[CacheBuster] Последняя версия: ${latestVersion}`);
      
      // Сравниваем версии
      if (currentAppVersion !== latestVersion) {
        console.log('[CacheBuster] Обнаружена новая версия! Очищаем кеш и перезагружаем...');
        
        // Очищаем все кеши
        await clearAllCaches();
        
        // Сохраняем новую версию
        localStorage.setItem(CACHE_BUSTER_STORAGE_KEY, latestVersion);
        
        // Показываем уведомление и перезагружаем
        setIsUpdateAvailable(true);
        setReloadFn(() => forceReload);
        
        // Можно автоматически перезагрузить через 1 секунду
        setTimeout(() => {
          forceReload();
        }, 1000);
      } else {
        console.log('[CacheBuster] Версии совпадают, всё ок');
        setIsUpdateAvailable(false);
      }
    } catch (error) {
      console.error('[CacheBuster] Ошибка проверки версии:', error);
      // Если meta.json нет, просто показываем приложение
    } finally {
      setIsChecking(false);
    }
  }, [currentAppVersion, clearAllCaches, forceReload]);

  // Запускаем проверку при монтировании
  useEffect(() => {
    checkVersion();
  }, [checkVersion]);

  // Показываем загрузку
  if (isChecking && loadingComponent) {
    return loadingComponent;
  }

  // Показываем уведомление о новой версии
  if (isUpdateAvailable) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
        fontFamily: 'sans-serif'
      }}>
        <div>🔄 Доступна новая версия приложения</div>
        <div style={{ fontSize: '14px', color: '#666' }}>
          Обновление через несколько секунд...
        </div>
        <button
          onClick={() => reloadFn && reloadFn()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Обновить сейчас
        </button>
      </div>
    );
  }

  return children;
};

export default CacheBuster;